/**
 * Office (location) scope resolution and per-office rollups (ADR-0012).
 *
 * A practice has many offices. The top-bar office selector picks one office
 * or "All offices". These helpers turn that selection into the concrete list
 * of location ids a query may touch, and roll per-office metrics up into the
 * "each office plus all offices combined" shape every dashboard shows.
 *
 * Fail-closed: anything unknown or not granted resolves to no locations,
 * never to "every location in the practice".
 */
import type { LocationScope } from '@velora/contracts';

/** URL search-param value for the all-offices view (`?office=all`). */
export const ALL_OFFICES_PARAM = 'all';

/**
 * Location ids a request may read for the selected scope.
 *
 * @param scope the office selector value
 * @param accessibleLocationIds locations granted to the user by
 *   `user_location_access` (already filtered to active locations)
 * @returns ids in the order of `accessibleLocationIds`; empty when the scope
 *   names a location the user may not access, or when the user has none
 */
export function resolveLocationScope(
  scope: LocationScope,
  accessibleLocationIds: readonly string[],
): readonly string[] {
  const unique = [...new Set(accessibleLocationIds)];
  if (scope.kind === 'all') return unique;
  return unique.includes(scope.locationId) ? [scope.locationId] : [];
}

/** Serialises a scope for the URL (`office=all` or `office=<uuid>`). */
export function scopeToParam(scope: LocationScope): string {
  return scope.kind === 'all' ? ALL_OFFICES_PARAM : scope.locationId;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Parses the URL param. Returns `null` for anything malformed so the caller
 * falls back to the user's default office instead of guessing.
 */
export function scopeFromParam(param: string | null | undefined): LocationScope | null {
  if (param === undefined || param === null) return null;
  const value = param.trim();
  if (value.toLowerCase() === ALL_OFFICES_PARAM) return { kind: 'all' };
  if (UUID_RE.test(value)) return { kind: 'location', locationId: value.toLowerCase() };
  return null;
}

/**
 * Picks the scope to show when the user opens the app or switches practice:
 * the remembered scope if still valid, else the default office, else the
 * first accessible office, else `null` (the user can see nothing).
 * Users with access to a single office never land on "All offices".
 */
export function chooseInitialScope(
  remembered: LocationScope | null,
  accessibleLocationIds: readonly string[],
  defaultLocationId?: string | null,
): LocationScope | null {
  if (accessibleLocationIds.length === 0) return null;
  if (remembered) {
    if (remembered.kind === 'all' && accessibleLocationIds.length > 1) return remembered;
    if (remembered.kind === 'location' && accessibleLocationIds.includes(remembered.locationId)) {
      return remembered;
    }
  }
  if (defaultLocationId && accessibleLocationIds.includes(defaultLocationId)) {
    return { kind: 'location', locationId: defaultLocationId };
  }
  const first = accessibleLocationIds[0];
  return first === undefined ? null : { kind: 'location', locationId: first };
}

/** A metric row for one office. Metric values are numbers (counts or integer cents). */
export type OfficeMetricRow<K extends string> = { locationId: string } & Record<K, number>;

export interface OfficeRollup<K extends string> {
  /** One row per office in scope, in scope order; offices with no data are zero-filled. */
  perOffice: Array<OfficeMetricRow<K>>;
  /** Sum across `perOffice`. Ratios must be recomputed from summed parts, never averaged. */
  total: Record<K, number>;
}

/**
 * Builds the "each office and all offices combined" view used by dashboards.
 * Rows for locations outside `locationIds` are dropped (defence in depth on
 * top of RLS); duplicate rows for one office are summed.
 */
export function rollupByOffice<K extends string>(
  rows: ReadonlyArray<OfficeMetricRow<K>>,
  locationIds: readonly string[],
  metrics: readonly K[],
): OfficeRollup<K> {
  const zero = (): Record<K, number> => {
    const r = {} as Record<K, number>;
    for (const k of metrics) r[k] = 0;
    return r;
  };
  const byOffice = new Map<string, Record<K, number>>(locationIds.map((id) => [id, zero()]));
  for (const row of rows) {
    const acc = byOffice.get(row.locationId);
    if (!acc) continue;
    for (const k of metrics) {
      const v = row[k];
      if (!Number.isFinite(v))
        throw new RangeError(`metric "${k}" for ${row.locationId} is not finite`);
      acc[k] += v;
    }
  }
  const total = zero();
  const perOffice: Array<OfficeMetricRow<K>> = [];
  for (const id of locationIds) {
    const acc = byOffice.get(id) ?? zero();
    for (const k of metrics) total[k] += acc[k];
    perOffice.push({ locationId: id, ...acc } as OfficeMetricRow<K>);
  }
  return { perOffice, total };
}
